import { ChevronDown, Loader2, PhoneOff, Voicemail, X, type LucideIcon } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  SUGGESTION_SHOWN_MS, VOICEMAIL_LEFT_OUTCOME, VOICEMAIL_OUTCOME, canDropVoicemail, cadenceSays, greetingLine,
  greetingVersion, leaves, offersCadenceVoicemail, suggestionPlan, suggestionRings, suggestionTimesOut,
  suggestsVoicemail, type SuggestionPlan,
} from "@/lib/dialer";
import {
  useCallDetection, useDropVoicemail, useLeadCard, useVoicemailMessages,
} from "@/lib/queries";
import type { VoicemailMessage } from "@/lib/types";
import { useMediaQuery } from "@/lib/use-media-query";
import { cn } from "@/lib/utils";
import { useAcdCall } from "@/softphone/acd-call";
import {
  dismissSuggestion, forgetPlannedOutcome, planOutcome, useDismissedSuggestions,
} from "@/softphone/planned-outcome";
import { RAIL_CELL } from "@/softphone/rail";
import { useSoftphone } from "@/softphone/softphone-context";

/** From here up the phone has a rail (`softphone-dock.tsx`), and the suggestion opens from it. */
export const PHONE_DOCKED = "(min-width: 48rem)";

/** The mark on everything the suggestion draws outside the phone's own popover, so the phone does
 *  not read a press on it as a click somewhere else on the page and close itself. */
export const PHONE_COMPANION = "data-phone-companion";

/** The phone's popover is this wide and sits this far from the rail; the suggestion opens beside
 *  it rather than over it. `w-80` and `sideOffset={8}` in `softphone-dock.tsx`. */
const BESIDE_THE_RAIL = 8;
const BESIDE_THE_PHONE = BESIDE_THE_RAIL + 320 + 8;

/**
 * "Possible voicemail", on a campaign whose answering machine detection is passive (ADR-106 in
 * `platform`).
 *
 * Passive detection decides nothing. The rep is on the call from the answer, the switch listens
 * beside it, and when it hears what sounds like a voicemail greeting the dialer tells this phone,
 * which offers three things: leave a message, hang up, or say it is a person. Whatever the rep
 * chooses is the rep's choice; the suggestion only saves them the two clicks that follow it.
 * Leaving a message records the outcome Voicemail left and hanging up records Voicemail, so the
 * outcome window does not ask (`planOutcome`, read by `DispositionView`).
 *
 * The detection goes by sound alone, so the suggestion also comes up for a person who answers
 * with a whole sentence. That is why it is a suggestion with a way to wave it off, in the warning
 * tone rather than the danger one, and why it never takes the call's own controls away.
 *
 * It follows the cadence (ADR-107). The cadence says what each call and each redial does on a
 * voicemail: hang up, after which a redial may follow at once, or leave a message, and which. The
 * suggestion makes that choice the main button and says it in a sentence (`suggestionPlan`); the
 * other stays beside it, in the same place on every call, and the rep still chooses. The main
 * button takes the room in the row and the other only what its label needs (`Choice`).
 *
 * Where it is drawn. On a desktop-width window it is the phone rail's second button
 * (`VoicemailRailButton`): a voicemail icon that appears under the phone's own button and opens
 * the choices as a popover of its own, the way the phone opens from its button. It opens itself
 * when the suggestion arrives and stays until the rep chooses or closes it, and the icon stays in
 * the rail for as long as the suggestion stands, to open the choices again: closing them is not
 * an answer the rep cannot take back. A narrow window has no rail, so there the same choices are
 * a card at the foot of the phone's call view and of the Phone page's column
 * (`VoicemailSuggestion`), and its X hides the card for the call, there being no icon to come
 * back from.
 *
 * The same place offers the cadence's message where nothing listens to the call (ACL-350). On a
 * campaign with detection off every answer reaches the rep, voicemail or not, and nothing can
 * suggest anything. But the cadence may have set this attempt to leave a message, and the rep
 * cannot know that from the call. So on such an attempt the choices are there from the moment
 * the rep is connected, in the accent tone since nothing was heard: "If this is a voicemail",
 * with the message to leave. There is no Hang up beside it, because nothing says it is a
 * voicemail: the call's own End is where it always is, and a person who answered is just talked
 * to.
 *
 * A greeting written as text (ADR-128). Where the tenant has a voice set up, core-api picks the
 * call's message with such greetings in the running, fills one for this lead in the rep's name,
 * and has it spoken while the suggestion is on the screen. The detection names it (`greeting`),
 * and that is what Leave voicemail plays: the phone's own list holds recordings only, and they
 * stay one click away. The words are shown, so the rep knows what the lead will hear, and can
 * say them when the sound could not be made. A click before the sound is ready waits for it on
 * the server; a greeting that failed says why, and the recordings are still there.
 */
interface Suggestion {
  /** The lead's channel: what the detection, the drop and the outcome all name. */
  callId: string;
  /** The switch heard what sounds like a voicemail. False: nothing listens to this call, and the
   *  cadence leaves a message on this attempt. */
  heard: boolean;
  /** Whether the attempt is a redial of its call, for the sentence that names it. */
  redial: boolean;
  /** What this attempt offers, by the cadence: the main button, the message, the redial. */
  plan: SuggestionPlan<VoicemailMessage>;
  busy: "leave" | "hangup" | null;
  error: string | null;
  /** The rep closed the choices with the X on this call: it stops calling for one. */
  waved: boolean;
  /** A message of the library by its slug: a recording, or the greeting core-api prepared. */
  leave: (message: { slug: string; name: string }) => void;
  hangUp: () => void;
  dismiss: () => void;
}

/** Nobody: the calls a surface that keeps a waved-off suggestion leaves out. */
const NO_CALLS: ReadonlySet<string> = new Set();

/**
 * The suggestion for the call the rep is on, or null while there is nothing to suggest.
 *
 * `kept` is the rail's: a suggestion the rep closed with the X stays, as a still icon that opens
 * the choices again. Without it, such a suggestion is gone for the call, which is the card of a
 * narrow window, where nothing is left on screen to bring it back from.
 */
function useVoicemailSuggestion(kept = false): Suggestion | null {
  const phone = useSoftphone();
  const toast = useToast();
  const acd = useAcdCall();
  const live = canDropVoicemail(acd) ? acd : null;
  const detection = useCallDetection(live?.callerChannelId ?? null, live !== null);
  const dismissed = useDismissedSuggestions();
  const left = kept ? NO_CALLS : dismissed;
  const heard = suggestsVoicemail(detection.data, acd, left);
  const show = heard || offersCadenceVoicemail(detection.data, acd, left);
  const messages = useVoicemailMessages(show);
  // The lead's own message (ADR-061): the query the lead card already runs for this call.
  const card = useLeadCard(
    show ? (acd?.leadId ?? null) : null,
    acd?.sessionId ?? acd?.callerChannelId ?? null,
  ).data;
  const drop = useDropVoicemail();
  const [busy, setBusy] = React.useState<"leave" | "hangup" | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  if (!show || acd === null) return null;
  const callId = acd.callerChannelId;
  const suggested = card?.suggested_voicemail ?? null;
  const library = messages.data ?? [];

  const leave = async (message: { slug: string; name: string }) => {
    setError(null);
    setBusy("leave");
    planOutcome(callId, VOICEMAIL_LEFT_OUTCOME);
    try {
      await drop.mutateAsync({ callId, template: message.slug });
      toast.success("Leaving a voicemail", message.name);
    } catch (e) {
      forgetPlannedOutcome(callId);
      setError(e instanceof ApiError ? e.message : "Could not leave the message.");
    } finally {
      setBusy(null);
    }
  };

  const hangUp = async () => {
    setError(null);
    setBusy("hangup");
    planOutcome(callId, VOICEMAIL_OUTCOME);
    try {
      await phone.hangup(acd.callId ?? undefined);
    } catch {
      forgetPlannedOutcome(callId);
      setError("Could not hang up.");
    } finally {
      setBusy(null);
    }
  };

  return {
    callId,
    heard,
    redial: (detection.data?.redial?.n ?? 0) > 0,
    plan: suggestionPlan(detection.data, library, suggested),
    busy,
    error,
    waved: dismissed.has(callId),
    leave: (message) => void leave(message),
    hangUp: () => void hangUp(),
    dismiss: () => dismissSuggestion(callId),
  };
}

/**
 * One of the row's two buttons. The main one takes all the room the other leaves and carries the
 * icon, so its label has air around it; the other is a size down, text only, and as wide as its
 * label. Which is which follows the cadence, and their places do not: Leave voicemail is on the
 * left on every call. (As two equal halves, "Leave voicemail" filled its button edge to edge
 * beside a half-empty Hang up. Mustafa, 2026-10-04: "the highlighted button should get more
 * space, and the one which is secondary should get less space".)
 *
 * While it works, the spinner takes the icon's place on the main one and the label's place on the
 * other, so neither changes width under the rep's pointer.
 */
function Choice({
  main, working, icon: Icon, children, ...props
}: {
  main: boolean;
  working: boolean;
  icon: LucideIcon;
  children: string;
} & Pick<React.ComponentProps<typeof Button>, "disabled" | "title" | "onClick">) {
  return (
    <Button
      variant={main ? "default" : "outline"}
      size={main ? "md" : "sm"}
      className={main ? "h-9 min-w-0 flex-1 gap-2 px-4" : "relative h-9 px-3"}
      aria-busy={working || undefined}
      {...props}
    >
      {main && (working ? <Loader2 className="animate-spin" aria-hidden /> : <Icon aria-hidden />)}
      {!main && working && <Loader2 className="absolute animate-spin" aria-hidden />}
      <span className={cn(!main && working && "invisible")}>{children}</span>
    </Button>
  );
}

/** The name and the tone of the two things this draws: a voicemail the switch heard, in the
 *  warning tone, and a message the cadence leaves where nothing listens, in the accent one. */
function looks(heard: boolean) {
  return heard
    ? {
        name: "Possible voicemail",
        box: "border-warning-6 bg-warning-2",
        icon: "bg-warning-4 text-warning-11",
        hover: "hover:bg-warning-4",
        rail: "bg-warning-4 text-warning-11 hover:bg-warning-5 [--pulse-ring:var(--color-warning-9)]",
      }
    : {
        name: "Voicemail message for this call",
        box: "border-accent-6 bg-accent-2",
        icon: "bg-accent-4 text-accent-11",
        hover: "hover:bg-accent-4",
        rail: "bg-accent-3 text-accent-11 hover:bg-accent-4 [--pulse-ring:var(--color-accent-9)]",
      };
}

/**
 * What the suggestion says and offers, the same wherever it is drawn. The X is the surface's own
 * (`close`): in the rail it puts the choices away and the icon brings them back; in a narrow
 * window's card it says the call is a person, and the card is gone for the call.
 */
function Choices({
  suggestion, close, onMenuOpenChange,
}: {
  suggestion: Suggestion;
  close: { label: string; run: () => void };
  /** Told when the list of other messages opens and closes: it is drawn outside the choices, so
   *  a pointer in it has left them. */
  onMenuOpenChange?: (open: boolean) => void;
}) {
  const { plan, busy, error, heard } = suggestion;
  const { others, redial, greeting } = plan;
  // What Leave voicemail plays: the greeting core-api prepared for this call, else a recording.
  const first = leaves(plan);
  const stands = greetingLine(greeting);
  // Which of a greeting's two texts this call was given (ADR-116); null when it has one.
  const version = greetingVersion(greeting);
  const says = cadenceSays(plan);
  // Nothing was heard: Leave voicemail is the one thing offered, whatever the plan's main is.
  const leaveIsMain = !heard || plan.main === "leave";
  const look = looks(heard);
  return (
    <>
      <div className="flex items-start gap-2.5">
        <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-full", look.icon)}>
          <Voicemail className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1" role="status">
          <p className="text-sm font-semibold text-text">
            {heard ? "Possible voicemail" : "If this is a voicemail"}
          </p>
          <p className="text-xs text-text-muted">
            {heard
              ? "This sounds like a voicemail greeting. Nothing happens until you choose."
              : `The cadence leaves a message on this ${suggestion.redial ? "redial" : "call"}.`}
          </p>
        </div>
        <button
          type="button"
          className={cn("-mr-1 -mt-1 rounded p-1 text-text-muted hover:text-text", look.hover)}
          aria-label={close.label}
          title={close.label}
          onClick={close.run}
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>

      <div className="mt-3.5 flex gap-2">
        <Choice
          main={leaveIsMain}
          icon={Voicemail}
          working={busy === "leave"}
          disabled={first === null || busy !== null}
          title={
            first === null
              ? "No voicemail messages yet. An admin adds them to the library."
              : `Plays “${first.name}” to their voicemail, and your call ends`
          }
          onClick={() => first && suggestion.leave(first)}
        >
          Leave voicemail
        </Choice>
        {heard && (
          <Choice
            main={!leaveIsMain}
            icon={PhoneOff}
            working={busy === "hangup"}
            disabled={busy !== null}
            title={
              redial
                ? `Ends your call, and the dialer calls them again at once: redial ${redial.next} of ${redial.of}`
                : "Ends your call"
            }
            onClick={suggestion.hangUp}
          >
            Hang up
          </Choice>
        )}
      </div>

      {greeting?.kind === "script" && greeting.words && (
        // What the lead will hear, so the rep knows it before leaving it, and can say it when
        // the sound could not be made.
        <figure className="mt-3 rounded-md border border-border bg-surface px-3 py-2">
          <figcaption className="flex flex-wrap items-center gap-1.5 text-2xs font-medium text-text-muted">
            What “{greeting.name}” says
            {version && (
              <Badge tone={version.second ? "warning" : "outline"} size="sm" title={version.why}>
                {version.label}
              </Badge>
            )}
          </figcaption>
          <blockquote className="mt-1 max-h-32 overflow-y-auto text-xs leading-relaxed text-text">
            {greeting.words}
          </blockquote>
        </figure>
      )}
      {stands && (
        <p
          role={greeting?.status === "failed" ? "alert" : "status"}
          className={cn(
            "mt-1.5 text-2xs",
            greeting?.status === "failed" ? "text-danger-11" : "text-text-muted",
          )}
        >
          {stands}
        </p>
      )}

      {heard ? (
        <>
          {says && <p className="mt-3 text-xs font-medium text-text">{says}</p>}
          <p className={cn("text-2xs text-text-muted", says ? "mt-0.5" : "mt-3")}>
            {first ? `Leaving “${first.name}” records Voicemail left.` : "No voicemail messages yet."}{" "}
            Hanging up records Voicemail.
          </p>
        </>
      ) : (
        <p className="mt-3 text-2xs text-text-muted">
          {first
            ? `“${first.name}” plays, your call ends by itself, and Voicemail left is recorded.`
            : "No voicemail messages yet."}{" "}
          If a person answered, just talk.
        </p>
      )}
      {others.length > 0 && (
        <DropdownMenu onOpenChange={onMenuOpenChange}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="mt-1 inline-flex items-center gap-0.5 text-2xs font-medium text-accent-11 hover:underline disabled:opacity-50"
              disabled={busy !== null}
            >
              Leave another message
              <ChevronDown className="size-3" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" {...{ [PHONE_COMPANION]: "" }}>
            {others.map((message) => (
              <DropdownMenuItem key={message.id} onSelect={() => suggestion.leave(message)}>
                Leave “{message.name}”
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-danger-11">
          {error}
        </p>
      )}
    </>
  );
}

/** The suggestion as a card in the page's flow: a narrow window, where the phone has no rail. */
export function VoicemailSuggestion({ className }: { className?: string }) {
  const docked = useMediaQuery(PHONE_DOCKED);
  const suggestion = useVoicemailSuggestion();
  if (docked || suggestion === null) return null;
  const look = looks(suggestion.heard);
  return (
    <div
      role="group"
      aria-label={look.name}
      className={cn("animate-rise-in rounded-lg border p-4 shadow-sm", look.box, className)}
    >
      <Choices
        suggestion={suggestion}
        close={{ label: "It is a person: hide this", run: suggestion.dismiss }}
      />
    </div>
  );
}

/**
 * The suggestion in the phone's rail: a voicemail icon under the phone's button, there for as
 * long as a voicemail is suggested on the call, and the choices in a popover that opens from it,
 * as the phone opens from its own button.
 *
 * It opens itself when the suggestion arrives, without taking the keyboard: the rep may be typing
 * a note. A click elsewhere does not close it, since what it asks is still open; the icon folds it
 * away and brings it back, Escape folds it away, and the icon keeps pulsing while it is folded.
 * The X closes it too, and the icon stays: it took the icon out of the rail once, for the rest of
 * the call, and a rep who closed the choices to see what was under them had no way back to Leave
 * voicemail. A suggestion closed with the X is one the rep has answered for now, so its icon is
 * still (`suggestionRings` in `lib/dialer.ts`), and a click on it opens the choices like any
 * other. While the phone's own popover is open it sits beside that popover, not over the call.
 *
 * Choices that opened by themselves also put themselves away, after twenty seconds
 * (`suggestionTimesOut`), exactly as the X would: they sit over whatever the rep was reading, and
 * a rep talking to a person does not stop to close them. Not while the rep is at them, and never
 * the ones the rep opened from the icon.
 */
export function VoicemailRailButton({
  phoneOpen, onShownChange,
}: {
  phoneOpen: boolean;
  /** Told whenever "a voicemail is suggested and its choices are open" changes, so the rail's
   *  other popover (`my-calls.tsx`) can make room for this one. */
  onShownChange?: (shown: boolean) => void;
}) {
  const suggestion = useVoicemailSuggestion(true);
  const callId = suggestion?.callId ?? null;
  const [open, setOpen] = React.useState(false);
  // Set when the REP opened it from the icon, which may take the keyboard; arriving may not.
  const byRep = React.useRef(false);
  // The same fact for what is drawn: choices that opened by themselves close by themselves.
  const [byItself, setByItself] = React.useState(false);
  // The rep is at the choices: the pointer on them, the keyboard in them, or their list open.
  const [pointed, setPointed] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [listOpen, setListOpen] = React.useState(false);
  React.useEffect(() => {
    byRep.current = false;
    setOpen(callId !== null);
    setByItself(callId !== null);
    setPointed(false);
    setFocused(false);
    setListOpen(false);
  }, [callId]);
  const shown = callId !== null && open;
  React.useEffect(() => {
    onShownChange?.(shown);
  }, [shown, onShownChange]);

  // What the X does, and what the clock does in its place: the choices go, the icon stays.
  const dismiss = suggestion?.dismiss;
  const close = React.useCallback(() => {
    dismiss?.();
    setOpen(false);
  }, [dismiss]);
  const timesOut =
    suggestion !== null &&
    suggestionTimesOut({
      open,
      byItself,
      attended: pointed || focused || listOpen,
      working: suggestion.busy !== null,
      failed: suggestion.error !== null,
    });
  React.useEffect(() => {
    if (!timesOut) return;
    // From the start each time the rep leaves them alone, not from where the clock had got to.
    const timer = setTimeout(close, SUGGESTION_SHOWN_MS);
    return () => clearTimeout(timer);
    // `close` changes with every render of the suggestion; the call it closes does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesOut, callId]);

  if (suggestion === null) return null;
  const companion = { [PHONE_COMPANION]: "" };
  const look = looks(suggestion.heard);
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        byRep.current = next;
        setByItself(false);
        setOpen(next);
      }}
    >
      <PopoverAnchor asChild>
        <div
          className={RAIL_CELL}
          {...companion}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`${look.name}. ${open ? "Hide" : "Show"} the choices`}
              title={look.name}
              className={cn(
                "relative flex size-9 items-center justify-center rounded-full transition-colors",
                look.rail,
              )}
            >
              <Voicemail className="size-[18px]" aria-hidden />
              {/* The ring calls for a choice on something heard. A message the cadence plans is
                  no alarm, so its icon stays still, and so does one the rep closed with the X. */}
              {suggestionRings(suggestion.heard, open, suggestion.waved) && (
                <span className="animate-pulse-ring" aria-hidden />
              )}
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={phoneOpen ? BESIDE_THE_PHONE : BESIDE_THE_RAIL}
        role="group"
        aria-label={look.name}
        className={cn("w-80 p-4", look.box)}
        {...companion}
        onOpenAutoFocus={(e) => {
          if (!byRep.current) e.preventDefault();
        }}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerEnter={() => setPointed(true)}
        onPointerLeave={() => setPointed(false)}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
        }}
      >
        <Choices
          suggestion={suggestion}
          close={{
            label: "Close. The voicemail icon beside the phone opens this again",
            run: close,
          }}
          onMenuOpenChange={setListOpen}
        />
      </PopoverContent>
    </Popover>
  );
}
